<template>
    <index-table
        :cols="[
            {
                label: 'Image(s)',
                searchable: false,
                sortable: false,
                customCell: 'RelayImagesCell',
            },
            {
                value: 'site_name',
                label: 'Name',
                searchable: true,
                sortable: true,
            },
            {
                value: 'type',
                label: 'Relay Type',
                searchable: true,
                sortable: true,
            },
            {
                value: 'description',
                label: 'Description',
                searchable: true,
                sortable: true,
                customCell: 'RelayDescriptionCell',
            },
            {
                value: 'serial_number',
                label: 'EdgeRelay SN',
                searchable: true,
                sortable: true,
            },
            {
                value: 'latest_health_check_at',
                label: 'Last Response',
                searchable: true,
                sortable: true,
                customCell: 'RelayRadioLastResponseCell',
            },
            {
                label: 'Status',
                searchable: false,
                sortable: false,
                customCell: 'RelayRadioStatusCell',
            },
            {
                value: 'installed_at',
                label: 'Install Date',
                searchable: true,
                sortable: true,
            },
        ]"
        :disableMultiSelect="false"
        :options="options"
        :optionsComponent="optionsComponent"
        :deleteHandler="deleteHandler"
        :title="'Relay Management'"
        alignCells="middle"
        data-test-id="relays-table"
        defaultSort="latest_health_check_at"
        defaultDir="desc"
        model="relays"
    >
        <template #create>
            <create-asset-menu />
        </template>
        <template #navigation>
            <div class="tabs">
                <button
                    class="active"
                    data-test-id="relays-table-button"
                    @click="goToRelays"
                >
                    Relays
                </button>
                <button
                    data-test-id="relay-radios-table-button"
                    @click="goToRelayRadios"
                >
                    Radios
                </button>
            </div>
        </template>

        <template #custom-action>
            <router-link
                v-if="shouldShowProvisionButton"
                to="relays/provision"
                class="btn btn-primary"
                data-test-id="provision-button"
                tag="button"
            >
                <span class="icon-save" /> Provision
            </router-link>
            <router-link
                to="relays/map"
                class="btn btn-primary"
                data-test-id="view-map-button"
                tag="button"
            >
                <span class="icon-map" /> View Map
            </router-link>
        </template>
    </index-table>
</template>

<script>
import canConfirm from '@/mixins/canConfirm'
import IndexTable from '@/components/IndexTable/index.vue'
import CreateAssetMenu from '@/components/CreateAssetMenu.vue'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import Permissions from '@/mixins/permissions'
import featureFlags from '@/constants/feature-flags'

export default {
    components: {
        IndexTable,
        CreateAssetMenu,
    },

    mixins: [canConfirm, needsOrganizationSlug, Permissions],

    data() {
        let selectedOrganizationId = null

        if (this.$route.query.organization_id) {
            selectedOrganizationId = parseInt(this.$route.query.organization_id, 10)
        }
        return {
            selectedOrganizationId,
            optionsComponent: 'RelayActions',
        }
    },

    computed: {
        options() {
            const result = []
            const { hasPermission } = this.$store.getters

            if (hasPermission('update assets')) {
                result.push('editable')
            }

            if (hasPermission('delete assets') || hasPermission('delete many assets')) {
                result.push('deletable')
            }

            if (hasPermission('upload assets')) {
                result.push('importable')
            }

            return result
        },
        shouldShowProvisionButton() {
            const { hasPermission, hasFeatureFlag } = this.$store.getters
            return hasPermission('create provisioning profiles') && hasFeatureFlag(featureFlags.ADVANCED_RELAYS_ENABLED)
        },
    },

    methods: {
        goToRelays() {
            this.$router.push(`/${this.organizationSlug}/relays`)
        },

        goToRelayRadios() {
            this.$router.push(`/${this.organizationSlug}/relays/radios`)
        },

        async deleteHandler(relays, showDefaultConfirmation, performDelete) {
            const many = relays.length > 1
            const relayVerbiage = many ? 'these relays' : 'this relay'

            const confirmed = await this.confirm({
                message: many
                    ? 'Are you sure you want to delete the selected Relays?'
                    : 'Are you sure you want to delete this Relay?',
                hint:
                    `You will also lose all health check data attached to ${relayVerbiage} and attached ` +
                    'radios. This action cannot be undone.',
                acceptLabel: 'Delete',
                danger: true,
            })

            if (confirmed) {
                performDelete(relays.map((g) => g.id))
            }
        },
    },
}
</script>
<style lang="scss">
.tabs {
    padding: 0px;
    margin: 0px;

    button {
        border: 1px solid #888;
        color: #fff;
        font-size: 14px;
        background-color: transparent;
        padding: 10px 12px;
        width: 256px;
        height: 40px;
        cursor: pointer;
    }

    .button:first-child {
        border-radius: 8px 0px 0px 8px;
    }

    button:not(:last-child) {
        border-right-width: 0px;
    }

    button:last-child {
        border-radius: 0px 8px 8px 0px;
    }

    button.active {
        background-color: #212628;
    }
}
</style>
